// Single-key shortcuts for the inbox. A key is ignored while the user types in
// a field, holds a modifier, or has a dialog or menu open, so a shortcut can
// never eat a letter of a reply. Each component registers the keys it owns.
import { useEffect, useRef } from 'react'

export type ShortcutMap = Partial<Record<string, () => void>>

function typingIn(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  return el.isContentEditable || el.closest('input, textarea, select, [role="combobox"], [role="listbox"]') !== null
}

export function useShortcuts(map: ShortcutMap, enabled = true) {
  const ref = useRef(map)
  useEffect(() => {
    ref.current = map
  })
  useEffect(() => {
    if (!enabled) return
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return
      if (e.key !== 'Escape' && typingIn(e.target)) return
      if (document.querySelector('[role="dialog"], [role="menu"], [role="listbox"]')) return
      const run = ref.current[e.key]
      if (!run) return
      e.preventDefault()
      run()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled])
}

// What the help dialog lists, in the order a person works.
export const inboxShortcuts: { keys: string[]; does: string }[] = [
  { keys: ['j'], does: 'Next conversation' },
  { keys: ['k'], does: 'Previous conversation' },
  { keys: ['r'], does: 'Write a reply' },
  { keys: ['t'], does: 'Send a template' },
  { keys: ['n'], does: 'Write a team note' },
  { keys: ['i'], does: 'Show or hide the customer panel' },
  { keys: ['Esc'], does: 'Leave the text box, or go back to the list on a phone' },
  { keys: ['Ctrl', 'Enter'], does: 'Send what you typed' },
  { keys: ['?'], does: 'Show these shortcuts' },
]
